import { create } from 'zustand';
import {
  LaborCostDashboardStore,
  TypesOfHoursEntry,
  HourTypesResponse,
  CalloutFulfillmentRate,
  PositionOvertime,
  TeamMemberTypeOvertime,
  DailyOvertimeDetail,
  WeeklyOvertimeDetail,
} from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { TotalShiftsResponse } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.types';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import {
  hasDataLockRejection,
  isDataLockError,
} from 'pages/ReportsPage/store/reportsDataHealth.store';
import { LABOR_COST_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.stub';
import {
  mapPeakCalloutHours,
  mapResolutionSpeed,
  sortByTimeframe,
  toTimeframeLabel,
  toNumber,
  PeakCalloutHoursResponseItem,
  CalloutResolutionPerTimeframeResponse,
} from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import {
  TotalDailyOvertimeHoursResponse,
  DailyOvertimeDetailRowResponseItem,
  DailyOvertimeDetailsResponse,
  WeeklyOvertimeDetailRowResponseItem,
  WeeklyOvertimeDetailsResponse,
  buildDailyOvertimeQuery,
  buildPayPeriodThroughRangeQuery,
} from 'pages/ReportsPage/components/AnalyticsDashboards/overtimeMappers';
import {
  DASHBOARDS_SHIFTS_UNFULFILLED_HOURS,
  DASHBOARDS_SHIFTS_SCHEDULED_HOURS,
  DASHBOARDS_SHIFTS_CALLOUT_HOURS,
  DASHBOARDS_SHIFTS_PEAK_CALLOUT_HOURS,
  DASHBOARDS_SHIFTS_CALLOUT_RESOLUTION_PER_TIMEFRAME,
  DASHBOARDS_SHIFTS_HOUR_TYPES,
  DASHBOARDS_SHIFTS_FULFILLMENT_BY_TIMEFRAME,
  DASHBOARDS_OVERTIME_TOTAL_DAILY_OVERTIME_HOURS,
  DASHBOARDS_OVERTIME_DAILY_OVERTIME_DETAILS,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_DETAILS,
} from 'constants/endpoints';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

const INITIAL_STATE = {
  calloutHours: 0,
  calloutHoursPeriodDays: null as number | null,
  calloutHoursChangePrct: null as number | null,
  calloutHoursError: false,
  dailyOvertimeHours: 0,
  dailyOvertimeHoursPeriodDays: null as number | null,
  dailyOvertimeHoursChangePrct: null as number | null,
  dailyOvertimeHoursError: false,
  unfulfilledHours: 0,
  unfulfilledHoursPeriodDays: null as number | null,
  unfulfilledHoursChangePrct: null as number | null,
  unfulfilledHoursError: false,
  scheduledHours: 0,
  scheduledHoursPeriodDays: null as number | null,
  scheduledHoursChangePrct: null as number | null,
  scheduledHoursError: false,
  typesOfHours: null,
  calloutTimeframeCells: null,
  calloutFulfillmentRate: null,
  resolutionSpeedCells: null,
  overtimeByPosition: null,
  overtimeByTeamMemberType: null,
  dailyOvertimeDetails: null,
  dailyOvertimeDetailsPage: 1,
  dailyOvertimeDetailsTotalPages: 1,
  dailyOvertimeDetailsLoading: false,
  dailyOvertimeDetailsError: false,
  weeklyOvertimeDetails: null,
  weeklyOvertimeDetailsPage: 1,
  weeklyOvertimeDetailsTotalPages: 1,
  weeklyOvertimeDetailsLoading: false,
  weeklyOvertimeDetailsError: false,
  isLoading: false,
  error: null,
};

const POSITIONS = [
  'Resident Care Partner (PSW)',
  'Wellness Nurse',
  'Medication Care Partner',
  'Server',
  'Housekeeper',
];
const TEAM_MEMBER_TYPES = ['Full Time', 'Part Time', 'Casual', 'Flex'];

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function mapHourTypes(response: HourTypesResponse): TypesOfHoursEntry[] {
  return (response.hour_types?.buckets ?? []).map((bucket) => ({
    date: bucket.bucket_start,
    unfulfilledHours: toNumber(bucket.unfulfilled_hours),
    agencyHours: toNumber(bucket.agency_hours),
    calloutHours: toNumber(bucket.callout_hours),
    assignedHours: toNumber(bucket.assigned_hours),
  }));
}

interface FulfillmentByTimeframeRow {
  shift_timeframe: string;
  fulfillment_pct: number | string;
}

function mapCalloutFulfillmentRate(
  response: FulfillmentByTimeframeRow[],
): CalloutFulfillmentRate[] {
  return sortByTimeframe(
    response.map((item) => ({
      timeframe: toTimeframeLabel(item.shift_timeframe),
      fulfillmentRate: toNumber(item.fulfillment_pct),
    })),
  );
}

function generateStubOvertimeByPosition(): PositionOvertime[] {
  return POSITIONS.map((position) => ({
    position,
    overtimeHours: randInt(50, 500),
  })).sort((a, b) => b.overtimeHours - a.overtimeHours);
}

function generateStubOvertimeByType(): TeamMemberTypeOvertime[] {
  const hours = TEAM_MEMBER_TYPES.map(() => randInt(100, 600));
  const total = hours.reduce((sum, h) => sum + h, 0);
  return TEAM_MEMBER_TYPES.map((type, i) => ({
    type,
    overtimeHours: hours[i],
    percentage: Math.round((hours[i] / total) * 10000) / 100,
  }));
}

export function toDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
}

export function mapDailyOvertimeDetails(
  rows: DailyOvertimeDetailRowResponseItem[],
): DailyOvertimeDetail[] {
  return rows.map((row) => ({
    day: toDisplayDate(row.day),
    location: row.jane_primary_community_name,
    teamMember: row.jane_name,
    totalDailyWorkingHours: toNumber(row.total_paid_hours),
    dailyLimit: toNumber(row.daily_limit),
    dailyOvertime: toNumber(row.total_overtime_hours),
  }));
}

export function mapWeeklyOvertimeDetails(
  rows: WeeklyOvertimeDetailRowResponseItem[],
): WeeklyOvertimeDetail[] {
  return rows.map((row) => ({
    weekStartDate: toDisplayDate(row.pay_period_start_at),
    location: row.jane_primary_community_name,
    teamMember: row.jane_name,
    totalWeeklyWorkingHours: toNumber(row.total_paid_hours),
    weeklyLimit: toNumber(row.weekly_limit),
    weeklyOvertime: toNumber(row.total_overtime_hours),
  }));
}

let fetchId = 0;
let dailyDetailsFetchId = 0;
let weeklyDetailsFetchId = 0;

export const laborCostDashboardStore = create<LaborCostDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchDailyOvertimeDetails: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++dailyDetailsFetchId;
      set({ dailyOvertimeDetailsLoading: true, dailyOvertimeDetailsError: false });

      const query = {
        ...buildDailyOvertimeQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<DailyOvertimeDetailsResponse>({
        url: DASHBOARDS_OVERTIME_DAILY_OVERTIME_DETAILS(),
        method: 'GET',
        query,
      }).then(
        ({ data }) => {
          if (currentFetchId !== dailyDetailsFetchId) return;
          set({
            dailyOvertimeDetails: mapDailyOvertimeDetails(data.daily_overtime_details ?? []),
            dailyOvertimeDetailsPage: data.meta?.current_page ?? page,
            dailyOvertimeDetailsTotalPages: data.meta?.total_pages ?? 1,
            dailyOvertimeDetailsLoading: false,
            dailyOvertimeDetailsError: false,
          });
        },
        (error) => {
          const isDataLocked = isDataLockError(error);
          if (currentFetchId !== dailyDetailsFetchId) return;
          if (isDataLocked) {
            set({
              dailyOvertimeDetails: LABOR_COST_DATA_LOCKED_STUB.dailyOvertimeDetails,
              dailyOvertimeDetailsPage: 1,
              dailyOvertimeDetailsTotalPages: 1,
              dailyOvertimeDetailsLoading: false,
              dailyOvertimeDetailsError: false,
            });
            return;
          }
          set({
            dailyOvertimeDetails: null,
            dailyOvertimeDetailsLoading: false,
            dailyOvertimeDetailsError: true,
          });
        },
      );
    },

    fetchWeeklyOvertimeDetails: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++weeklyDetailsFetchId;
      set({ weeklyOvertimeDetailsLoading: true, weeklyOvertimeDetailsError: false });

      const query = {
        ...buildPayPeriodThroughRangeQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<WeeklyOvertimeDetailsResponse>({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_DETAILS(),
        method: 'GET',
        query,
      }).then(
        ({ data }) => {
          if (currentFetchId !== weeklyDetailsFetchId) return;
          set({
            weeklyOvertimeDetails: mapWeeklyOvertimeDetails(data.weekly_overtime_details ?? []),
            weeklyOvertimeDetailsPage: data.meta?.current_page ?? page,
            weeklyOvertimeDetailsTotalPages: data.meta?.total_pages ?? 1,
            weeklyOvertimeDetailsLoading: false,
            weeklyOvertimeDetailsError: false,
          });
        },
        (error) => {
          const isDataLocked = isDataLockError(error);
          if (currentFetchId !== weeklyDetailsFetchId) return;
          if (isDataLocked) {
            set({
              weeklyOvertimeDetails: LABOR_COST_DATA_LOCKED_STUB.weeklyOvertimeDetails,
              weeklyOvertimeDetailsPage: 1,
              weeklyOvertimeDetailsTotalPages: 1,
              weeklyOvertimeDetailsLoading: false,
              weeklyOvertimeDetailsError: false,
            });
            return;
          }
          set({
            weeklyOvertimeDetails: null,
            weeklyOvertimeDetailsLoading: false,
            weeklyOvertimeDetailsError: true,
          });
        },
      );
    },

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;

      set({
        isLoading: true,
        error: null,
        calloutHoursError: false,
        dailyOvertimeHoursError: false,
        unfulfilledHoursError: false,
        scheduledHoursError: false,
      });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);

      const dailyOvertimeHoursRequest = createDashboardsRequest<TotalDailyOvertimeHoursResponse>({
        url: DASHBOARDS_OVERTIME_TOTAL_DAILY_OVERTIME_HOURS(),
        method: 'GET',
        query: buildDailyOvertimeQuery(dateRange, locationIds, positions, employmentTypes),
      });

      const unfulfilledHoursRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_UNFULFILLED_HOURS(),
        method: 'GET',
        query,
      });

      const scheduledHoursRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_SCHEDULED_HOURS(),
        method: 'GET',
        query,
      });

      const calloutHoursRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_CALLOUT_HOURS(),
        method: 'GET',
        query,
      });

      const peakCalloutHoursRequest = createDashboardsRequest<PeakCalloutHoursResponseItem[]>({
        url: DASHBOARDS_SHIFTS_PEAK_CALLOUT_HOURS(),
        method: 'GET',
        query,
      });

      const resolutionSpeedRequest = createDashboardsRequest<CalloutResolutionPerTimeframeResponse>(
        {
          url: DASHBOARDS_SHIFTS_CALLOUT_RESOLUTION_PER_TIMEFRAME(),
          method: 'GET',
          query,
        },
      );

      const hourTypesRequest = createDashboardsRequest<HourTypesResponse>({
        url: DASHBOARDS_SHIFTS_HOUR_TYPES(),
        method: 'GET',
        query,
      });

      const fulfillmentByTimeframeRequest = createDashboardsRequest<FulfillmentByTimeframeRow[]>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_TIMEFRAME(),
        method: 'GET',
        query,
      });

      Promise.allSettled([
        unfulfilledHoursRequest,
        scheduledHoursRequest,
        calloutHoursRequest,
        peakCalloutHoursRequest,
        resolutionSpeedRequest,
        hourTypesRequest,
        fulfillmentByTimeframeRequest,
        dailyOvertimeHoursRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...LABOR_COST_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          unfulfilledHoursResult,
          scheduledHoursResult,
          calloutHoursResult,
          peakCalloutHoursResult,
          resolutionSpeedResult,
          hourTypesResult,
          fulfillmentByTimeframeResult,
          dailyOvertimeHoursResult,
        ] = results;

        const unfulfilledHoursData = unwrapDashboardRoot<TotalShiftsResponse>(
          unfulfilledHoursResult.status === 'fulfilled' ? unfulfilledHoursResult.value.data : null,
          'unfulfilled_hours',
        );
        const scheduledHoursData = unwrapDashboardRoot<TotalShiftsResponse>(
          scheduledHoursResult.status === 'fulfilled' ? scheduledHoursResult.value.data : null,
          'scheduled_hours',
        );
        const calloutHoursData = unwrapDashboardRoot<TotalShiftsResponse>(
          calloutHoursResult.status === 'fulfilled' ? calloutHoursResult.value.data : null,
          'callout_hours',
        );
        const peakCalloutHoursData =
          peakCalloutHoursResult.status === 'fulfilled' ? peakCalloutHoursResult.value.data : null;
        const resolutionSpeedData =
          resolutionSpeedResult.status === 'fulfilled' ? resolutionSpeedResult.value.data : null;
        const hourTypesData =
          hourTypesResult.status === 'fulfilled' ? hourTypesResult.value.data : null;
        const fulfillmentByTimeframeData = unwrapDashboardRoot<FulfillmentByTimeframeRow[]>(
          fulfillmentByTimeframeResult.status === 'fulfilled'
            ? fulfillmentByTimeframeResult.value.data
            : null,
          'fulfillment_by_timeframe',
        );
        const dailyOvertimeHoursData = unwrapDashboardRoot<TotalDailyOvertimeHoursResponse>(
          dailyOvertimeHoursResult.status === 'fulfilled'
            ? dailyOvertimeHoursResult.value.data
            : null,
          'total_daily_overtime_hours',
        );

        set((state) => ({
          ...state,
          calloutHours: toNumber(calloutHoursData?.current_period_total),
          calloutHoursPeriodDays: calloutHoursData?.period_days ?? null,
          calloutHoursChangePrct:
            calloutHoursData?.change_prct == null ? null : toNumber(calloutHoursData.change_prct),
          calloutHoursError: calloutHoursResult.status === 'rejected',
          dailyOvertimeHours: toNumber(dailyOvertimeHoursData?.current_period_overtime),
          dailyOvertimeHoursPeriodDays: dailyOvertimeHoursData?.period_days ?? null,
          dailyOvertimeHoursChangePrct:
            dailyOvertimeHoursData?.change_prct == null
              ? null
              : toNumber(dailyOvertimeHoursData.change_prct),
          dailyOvertimeHoursError: dailyOvertimeHoursResult.status === 'rejected',
          unfulfilledHours: toNumber(unfulfilledHoursData?.current_period_total),
          unfulfilledHoursPeriodDays: unfulfilledHoursData?.period_days ?? null,
          unfulfilledHoursChangePrct:
            unfulfilledHoursData?.change_prct == null
              ? null
              : toNumber(unfulfilledHoursData.change_prct),
          unfulfilledHoursError: unfulfilledHoursResult.status === 'rejected',
          scheduledHours: toNumber(scheduledHoursData?.current_period_total),
          scheduledHoursPeriodDays: scheduledHoursData?.period_days ?? null,
          scheduledHoursChangePrct:
            scheduledHoursData?.change_prct == null
              ? null
              : toNumber(scheduledHoursData.change_prct),
          scheduledHoursError: scheduledHoursResult.status === 'rejected',
          typesOfHours: hourTypesData ? mapHourTypes(hourTypesData) : null,
          calloutTimeframeCells: peakCalloutHoursData
            ? mapPeakCalloutHours(peakCalloutHoursData)
            : null,
          calloutFulfillmentRate: fulfillmentByTimeframeData
            ? mapCalloutFulfillmentRate(fulfillmentByTimeframeData)
            : null,
          resolutionSpeedCells: resolutionSpeedData
            ? mapResolutionSpeed(resolutionSpeedData)
            : null,
          overtimeByPosition: generateStubOvertimeByPosition(),
          overtimeByTeamMemberType: generateStubOvertimeByType(),
          isLoading: false,
          error: null,
        }));
      });

      get().fetchDailyOvertimeDetails(1, dateRange, locationIds, positions, employmentTypes);
      get().fetchWeeklyOvertimeDetails(1, dateRange, locationIds, positions, employmentTypes);

      return () => {
        fetchId++;
        dailyDetailsFetchId++;
        weeklyDetailsFetchId++;
      };
    },
    setData: (data) => set((state) => ({ ...state, ...data })),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      dailyDetailsFetchId++;
      weeklyDetailsFetchId++;
      set(INITIAL_STATE);
    },
  })),
);

export const useLaborCostDashboardStore = laborCostDashboardStore;
